import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Clock, Compass, Coffee, Dumbbell, Leaf, Mountain, UtensilsCrossed, MapPin, Route } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { DESTINATIONS } from "@/data/platform";

const Block = ({ icon: Icon, title, items, accent }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-3xl p-7">
    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-4 ${accent ? "bg-[#C57B57]/10" : "bg-[#89937C]/10"}`}>
      <Icon size={19} className={accent ? "text-[#AD6A48]" : "text-[#89937C]"} />
    </div>
    <h3 className="font-serif-display text-xl text-[#2D372B] mb-3">{title}</h3>
    <ul className="space-y-2.5">
      {items.map((i) => (
        <li key={i} className="flex items-start gap-2.5 text-sm text-[#5C665A] leading-relaxed">
          <span className={`h-1.5 w-1.5 rounded-full mt-1.5 shrink-0 ${accent ? "bg-[#C57B57]" : "bg-[#89937C]"}`} />
          {i}
        </li>
      ))}
    </ul>
  </div>
);

export default function DestinationPage() {
  const { slug } = useParams();
  const d = DESTINATIONS.find((x) => x.slug === slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!d) return <Navigate to="/" replace />;
  const nearby = d.nearby.map((s) => DESTINATIONS.find((x) => x.slug === s)).filter(Boolean);

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <section className="relative h-[68vh] min-h-[440px] flex items-end overflow-hidden">
        <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A211A]/90 via-[#1A211A]/25 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-6 md:px-12 pb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C9CFC0] font-semibold mb-3">{d.region} · {d.state}</div>
          <h1 data-testid="destination-title" className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium text-[#F7F5F0] tracking-tight">{d.name}</h1>
          <p className="mt-3 text-lg text-[#E8C9B8] italic font-serif-display max-w-xl">{d.tagline}</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid lg:grid-cols-3 gap-8 mb-14">
          <p className="lg:col-span-2 text-lg leading-relaxed text-[#5C665A]">{d.intro}</p>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3 rounded-2xl bg-white border border-[#E5E2D9] px-5 py-4">
              <CalendarDays size={17} className="text-[#AD6A48]" />
              <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#89937C] font-semibold">Best Season</div><div className="text-sm font-medium">{d.bestSeason}</div></div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl bg-white border border-[#E5E2D9] px-5 py-4">
              <Clock size={17} className="text-[#AD6A48]" />
              <div><div className="text-[10px] uppercase tracking-[0.2em] text-[#89937C] font-semibold">Suggested Duration</div><div className="text-sm font-medium">{d.duration}</div></div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              {d.interests.map((i) => <span key={i} className="text-xs rounded-full bg-[#89937C]/10 border border-[#89937C]/30 px-3 py-1 text-[#5C665A]">{i}</span>)}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Block icon={Dumbbell} title="Fitness Experiences" items={d.fitness} />
          <Block icon={Leaf} title="Wellness & Recovery" items={d.wellness} />
          <Block icon={Coffee} title="The Coffee Trail" items={d.coffee} accent />
          <Block icon={Mountain} title="Adventure" items={d.adventure} />
          <Block icon={UtensilsCrossed} title="Eat Well" items={d.eats} />
          <div className="bg-[#2D372B] rounded-3xl p-7 text-[#F7F5F0]">
            <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center mb-4"><Route size={19} className="text-[#E8C9B8]" /></div>
            <h3 className="font-serif-display text-xl mb-3">A Journey Idea</h3>
            <p className="text-sm text-[#C9CFC0] leading-relaxed">{d.itinerary}</p>
            <Link to="/journey" state={{ interests: d.interests, destination: d.name }} data-testid="destination-plan-cta" className="mt-5 inline-flex items-center gap-2 rounded-full px-6 py-3 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] transition-colors">
              <Compass size={14} /> Personalize this journey
            </Link>
          </div>
        </div>

        <div className="mt-16">
          <div className="overline mb-6">Pairs Beautifully With</div>
          <div className="grid sm:grid-cols-2 gap-5 max-w-3xl">
            {nearby.map((n) => (
              <Link key={n.slug} to={`/destinations/${n.slug}`} data-testid={`nearby-${n.slug}`} className="group flex items-center gap-4 rounded-3xl bg-white border border-[#E5E2D9] p-4 hover:border-[#89937C] transition-colors">
                <img src={n.image} alt={n.name} loading="lazy" className="w-20 h-20 rounded-2xl object-cover" />
                <div className="min-w-0">
                  <div className="font-serif-display text-lg text-[#2D372B] flex items-center gap-1.5"><MapPin size={13} className="text-[#89937C]" /> {n.name}</div>
                  <p className="text-xs text-[#5C665A] line-clamp-2 mt-0.5">{n.tagline}</p>
                </div>
                <ArrowUpRight size={16} className="ml-auto shrink-0 text-[#89937C] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
